// ============================================================
// BLINKBLIS - CENTRO DE CONTROL DE OFERTAS
// ============================================================
// Este es el ÚNICO archivo que tocas para cambiar links de afiliado
// o el contenido de una oferta.
//
// TODO el contenido es bilingüe: cada campo lleva { es, en }.
//
// REGLA DE DATOS: cada cifra aquí salió de la página de la marca o de
// su ficha pública, no de una estimación. Si una cifra cambia y no la
// puedes verificar, se borra: no se aproxima.
// Datos de Kovo verificados el 19 de septiembre de 2026.
//
// REGLA DE LENGUAJE (cumplimiento):
// Prohibido en toda la página: "repara tu crédito", "sube tu puntaje",
// cifras de puntos, plazos, "aprobación garantizada", "elimina marcas
// negativas", testimonios.
// Permitido: "construir historial de pagos reportado", "puede ayudar",
// "los resultados varían", "no borra información negativa",
// "sin revisión de crédito", "reporta a los burós".
// ============================================================

import { attributionTag } from './attribution'

export type Lang = 'es' | 'en'
export type L<T> = Record<Lang, T>

export const pick = <T,>(value: L<T>, lang: Lang): T =>
  (value && value[lang]) ?? value.es

export type StuckBlock = {
  title: string
  paragraphs: string[]
  compareLabel: string
  cases: { t: string; d: string }[]
}

export type Offer = {
  slug: string // slug en español -> /oferta/<slug>
  enSlug: string // slug en inglés -> /offer/<enSlug>
  brand: string
  affiliateUrl: string // link crudo de impact, SIN subId (se agrega solo)
  network: string
  confidence: number
  updated: L<string>
  readMinutes: number
  category: L<string>
  title: L<string>
  subtitle: L<string>
  ctaLabel: L<string>
  ctaMicro: L<string> // línea corta bajo cada botón
  ctaDisclaimer: L<string> // párrafo de cumplimiento junto al CTA principal
  stuck: L<StuckBlock> // el ángulo de "llevo años estancado"
  facts: L<{ label: string; value: string }[]>
  glanceBestFor: L<string>
  glanceNotFor: L<string>
  bestFor: L<string[]>
  howItWorks: L<{ t: string; d: string }[]>
  pros: L<string[]>
  cons: L<string[]>
  notFor: L<string[]>
  faq: L<{ q: string; a: string }[]>
}

// ============================================================
// SUB ID
// ------------------------------------------------------------
// NO metas subIds en el formulario Advanced de impact: quedan fijos
// para siempre y no te dicen qué anuncio convirtió.
//
// En Meta Ads:
//   Website URL      -> https://blinkblis.com/oferta/credito   (ES)
//                       https://blinkblis.com/offer/credit      (EN)
//   URL parameters   -> utm_source={{site_source_name}}
//                       &utm_medium=paid_social
//                       &utm_campaign={{campaign.name}}
//                       &utm_content={{ad.name}}
//                       &campaign_id={{campaign.id}}
//                       &adset_id={{adset.id}}
//                       &ad_id={{ad.id}}
//
// Se lee en impact en: Reports -> More Reports ->
// "Performance by Sub ID and Shared ID", con Show -> Subid1.
// ============================================================
export const withSubId = (url: string, slug: string) => {
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}subId1=${encodeURIComponent(attributionTag(slug))}`
}

export const OFFERS: Offer[] = [
  {
    slug: 'credito',
    enSlug: 'credit',
    brand: 'Kovo',
    affiliateUrl: 'https://kovo-credit.sjv.io/enZ41Q',
    network: 'impact.com',
    confidence: 88,
    readMinutes: 7,
    updated: { es: 'Septiembre 2026', en: 'September 2026' },
    category: { es: 'Crédito', en: 'Credit' },
    title: {
      es: '¿Sin historial de crédito, o con un crédito estancado desde hace años?',
      en: 'No credit history — or stuck credit that has not moved in years?',
    },
    subtitle: {
      es: 'Empieza a generar actividad de crédito reportada por $10 al mes con Kovo. Sin revisión de crédito, sin depósito, y con los pagos reportados a Experian, Equifax, TransUnion e Innovis. No borra información negativa, pero puede ayudarte a sumar historial de pagos positivo con el tiempo.',
      en: 'Start building reported credit activity for $10/month with Kovo. No credit check, no deposit, and payment activity reported to Experian, Equifax, TransUnion and Innovis. It will not erase negative marks, but it may help you add positive payment history over time.',
    },
    ctaLabel: {
      es: 'Empezar con Kovo',
      en: 'Start with Kovo',
    },
    ctaMicro: {
      es: 'Enlace de afiliado. Blinkblis puede recibir una comisión. Tu precio no cambia.',
      en: 'Affiliate link. Blinkblis may earn a commission. Your price does not change.',
    },
    ctaDisclaimer: {
      es: 'Los resultados varían. Los pagos a tiempo pueden ayudar a tu perfil de crédito. Los pagos tardíos o no realizados pueden perjudicarlo. Blinkblis puede recibir una comisión si abres una cuenta a través de nuestro enlace.',
      en: 'Results vary. On-time payments may help your credit profile. Late or missed payments may hurt your credit. Blinkblis may earn a commission if you open an account through our link.',
    },
    stuck: {
      es: {
        title: '¿Llevas años y sigues igual?',
        paragraphs: [
          'A veces el problema no es que no trabajes duro ni que no pagues tus cuentas. El problema es que la actividad de pago correcta no se está reportando a los burós de crédito.',
          'Si han pasado años y tu perfil de crédito sigue sin moverse, agregar una cuenta de pagos que sí se reporte puede ayudarte a empezar a construir historial positivo otra vez. Kovo existe para eso específicamente: generar actividad de pago mensual que se reporta a los burós.',
          'Importante: esto no elimina colecciones, pagos tardíos, charge-offs ni ninguna otra información negativa. No es reparación de crédito. Es una forma de empezar a sumar historial de pagos positivo reportado, con el tiempo.',
        ],
        compareLabel: 'Dos situaciones comunes:',
        cases: [
          {
            t: 'Sin historial de crédito',
            d: 'Llegaste hace poco a Estados Unidos o nunca has usado crédito. El problema es la invisibilidad: no hay suficiente actividad reportada.',
          },
          {
            t: 'Crédito estancado o dañado',
            d: 'Puedes tener marcas negativas viejas o un expediente delgado. Kovo no elimina esas marcas, pero los pagos a tiempo reportados pueden ayudarte a sumar actividad positiva nueva con el tiempo.',
          },
        ],
      },
      en: {
        title: 'Still stuck after years?',
        paragraphs: [
          'Sometimes the problem is not that you do not work hard or that you do not pay your bills. The problem is that the right payment activity is not being reported to the credit bureaus.',
          'If years have passed and your credit profile still feels stuck, adding a simple reported payment account may help you start building positive history again. Kovo is designed for that specific purpose: creating monthly payment activity that is reported to the bureaus.',
          'Important: this does not erase collections, late payments, charge-offs or other negative information. It is not credit repair. It is a way to begin adding reported positive payment history over time.',
        ],
        compareLabel: 'Two common situations:',
        cases: [
          {
            t: 'No credit history',
            d: 'You are new to the U.S. or have never used credit. The issue is invisibility: there is not enough reported activity.',
          },
          {
            t: 'Stuck or damaged credit',
            d: 'You may have old negative marks or a thin file. Kovo will not remove those marks, but on-time reported payments may help you add new positive activity over time.',
          },
        ],
      },
    },
    facts: {
      es: [
        { label: 'Costo', value: '$10 al mes por 24 meses ($240 en total)' },
        { label: 'Intereses', value: '0%. Sin cargos ocultos' },
        { label: 'Revisión de crédito', value: 'No' },
        { label: 'Depósito inicial', value: 'No requiere' },
        {
          label: 'Reporta a',
          value: 'Experian, Equifax, TransUnion e Innovis',
        },
        {
          label: 'Incluye',
          value: 'Seguimiento de FICO y monitoreo de crédito',
        },
        { label: 'Si te arrepientes', value: 'Reembolso dentro de los 30 días' },
      ],
      en: [
        { label: 'Cost', value: '$10 a month for 24 months ($240 total)' },
        { label: 'Interest', value: '0%. No hidden fees' },
        { label: 'Credit check', value: 'No' },
        { label: 'Upfront deposit', value: 'Not required' },
        {
          label: 'Reports to',
          value: 'Experian, Equifax, TransUnion and Innovis',
        },
        {
          label: 'Includes',
          value: 'FICO score tracking and credit monitoring',
        },
        { label: 'If you change your mind', value: 'Refund within 30 days' },
      ],
    },
    glanceBestFor: {
      es: 'Personas que empiezan su historial de crédito desde cero, o que llevan años sin movimiento',
      en: 'People starting credit history from zero, or stuck without movement for years',
    },
    glanceNotFor: {
      es: 'Quien necesita efectivo, reparación de crédito o que desaparezcan sus marcas negativas',
      en: 'People needing cash, credit repair, or old negative marks to disappear',
    },
    bestFor: {
      es: [
        'Llegaste hace poco a Estados Unidos',
        'No tienes historial de crédito',
        'Tu perfil de crédito lleva años estancado',
        'No logras que te aprueben una tarjeta de crédito inicial',
        'Quieres que tu actividad de pago se reporte a los burós de crédito',
        'Puedes sostener $10 al mes durante 24 meses',
        'Entiendes que esto no elimina información negativa',
      ],
      en: [
        'You are new to the United States',
        'You have no credit history',
        'Your credit profile has been stuck for years',
        'You cannot get approved for a starter credit card',
        'You want payment activity reported to credit bureaus',
        'You can afford $10/month for 24 months',
        'You understand this does not erase negative marks',
      ],
    },
    howItWorks: {
      es: [
        {
          t: 'Abres tu cuenta en Kovo',
          d: 'Sin revisión de crédito y sin depósito. El proceso es en línea y toma unos minutos.',
        },
        {
          t: 'Pagas $10 al mes',
          d: 'Tu actividad de pago se reporta mensualmente a los cuatro burós.',
        },
        {
          t: 'Se acumula historial de pagos reportado',
          d: 'Los pagos a tiempo pueden ayudar a tu perfil de crédito con el tiempo. Los resultados varían.',
        },
      ],
      en: [
        {
          t: 'Open your Kovo account',
          d: 'No credit check or deposit required. The process is online and takes a few minutes.',
        },
        {
          t: 'Pay $10/month',
          d: 'Your payment activity is reported monthly to all four bureaus.',
        },
        {
          t: 'Build reported payment history over time',
          d: 'On-time payments may help your credit profile over time. Results vary.',
        },
      ],
    },
    pros: {
      es: [
        'Costo mensual bajo frente a muchas alternativas para construir crédito',
        'Reporta a los cuatro burós, no a uno solo. La mayoría de los productos de esta categoría reportan a menos',
        'No requiere revisión de crédito ni depósito para abrir la cuenta',
        'Sin intereses: el costo total es fijo y conocido desde el inicio',
        'Pagos automáticos, sin depender de recordar la fecha cada mes',
        'Incluye seguimiento de FICO y monitoreo de crédito sin costo adicional',
      ],
      en: [
        'Low monthly cost compared with many credit-building alternatives',
        'Reports to all four bureaus, not just one. Most products in this category report to fewer',
        'No credit check and no deposit required to open the account',
        'No interest: the total cost is fixed and known from the start',
        'Automatic payments, so the due date does not depend on memory',
        'FICO score tracking and credit monitoring included at no extra cost',
      ],
    },
    cons: {
      es: [
        'Es un compromiso de 24 meses. El historial no se compra, se acumula',
        'No es un préstamo: al final del plazo no recibes efectivo',
        'Un pago tardío aquí puede perjudicar tu crédito igual que en cualquier otra cuenta',
        'No elimina información negativa ni sustituye resolver colecciones o pagos atrasados',
        'La atención al cliente es limitada según reseñas públicas de terceros',
      ],
      en: [
        'It is a 24-month commitment. History is not bought, it accumulates',
        'It is not a loan: you do not receive cash at the end of the term',
        'A late payment here may hurt your credit just like any other account',
        'It does not erase negative marks and does not replace resolving collections or late payments',
        'Customer service is limited according to public third-party reviews',
      ],
    },
    notFor: {
      es: [
        'Esperas que desaparezcan marcas negativas viejas',
        'Necesitas reparación de crédito o servicios de disputa',
        'Tienes cuentas en colecciones que conviene atender primero',
        'Necesitas dinero de emergencia',
        'Esperas resultados en unas pocas semanas',
        'No puedes sostener cómodamente $10 al mes durante 24 meses',
        'Ya tienes un historial de crédito sólido',
      ],
      en: [
        'You expect old negative marks to disappear',
        'You need credit repair or dispute services',
        'You have active collections that need attention first',
        'You need emergency money',
        'You expect results in a few weeks',
        'You cannot comfortably afford $10/month for 24 months',
        'You already have solid credit history',
      ],
    },
    faq: {
      es: [
        {
          q: '¿Qué recibo exactamente por mis $10 al mes?',
          a: 'Es importante que quede claro: no es un préstamo y no recibes efectivo. Obtienes acceso a una cuenta de crédito a plazos cuyos pagos se reportan a los burós, más seguimiento de tu FICO y monitoreo de crédito. Lo que compras es el reporte, no el dinero.',
        },
        {
          q: 'Llevo años con el crédito estancado. ¿Esto me sirve?',
          a: 'Puede ayudarte a sumar actividad de pago positiva reportada, que es una parte de cómo se construye un perfil. Lo que no hace es eliminar lo negativo que ya tienes ni acelerar nada. Si tu situación son colecciones activas o pagos atrasados sin resolver, eso pesa más y conviene atenderlo primero.',
        },
        {
          q: '¿De verdad no hay revisión de crédito?',
          a: 'Kovo no realiza revisión de crédito para abrir la cuenta. Sí solicita datos básicos de identidad e ingreso para verificarte, pero eso no es una consulta a tu reporte de crédito.',
        },
        {
          q: '¿Cuánto tarda en verse reflejado?',
          a: 'Los burós actualizan en ciclos típicamente mensuales, y el historial se construye con el tiempo acumulado de pagos reportados. No existe un plazo garantizado y varía según cada situación. Cualquiera que prometa una cifra de puntos en una fecha está ofreciendo algo que no puede entregar.',
        },
        {
          q: '¿Esto elimina la información negativa que ya tengo?',
          a: 'No. Ningún producto de credit builder elimina información negativa exacta y verificable de un reporte de crédito. Construye historial nuevo; no borra el anterior.',
        },
        {
          q: '¿Garantiza que me aprueben una tarjeta o un auto después?',
          a: 'No. Ningún producto puede garantizar la aprobación de otro, y el aumento de puntaje tampoco está garantizado: los burós lo calculan con múltiples factores. Lo que hace es generar historial de pagos reportado, uno de los factores que los emisores consideran.',
        },
        {
          q: '¿Qué pasa si cancelo a los dos meses?',
          a: 'Existe reembolso dentro de los primeros 30 días. Pasado ese punto pierdes el beneficio principal, porque el historial se construye con pagos sostenidos: una cuenta abierta y cerrada rápido aporta muy poco.',
        },
        {
          q: '¿Por qué Blinkblis recomienda esto?',
          a: 'Porque para alguien que empieza desde cero o que lleva años sin movimiento cumple los cuatro criterios que evaluamos: entrada sin historial previo, costo bajo y fijo, reporte a los cuatro burós y términos claros antes de entregar datos personales. Recibimos una comisión si abres una cuenta, y eso está declarado en cada página.',
        },
      ],
      en: [
        {
          q: 'What exactly do I get for my $10 a month?',
          a: 'This matters, so plainly: it is not a loan and you do not receive cash. You get access to an installment credit account whose payments are reported to the bureaus, plus FICO score tracking and credit monitoring. What you are paying for is the reporting, not the money.',
        },
        {
          q: 'My credit has been stuck for years. Does this help?',
          a: 'It may help you add reported positive payment activity, which is one part of how a profile is built. What it does not do is remove the negative information you already have, or speed anything up. If your situation involves active collections or unresolved late payments, those weigh more and are worth addressing first.',
        },
        {
          q: 'Is there really no credit check?',
          a: 'Kovo does not run a credit check to open the account. It does collect basic identity and income information to verify you, but that is not an inquiry on your credit report.',
        },
        {
          q: 'How long until it shows up?',
          a: 'Bureaus typically update on monthly cycles, and history builds from accumulated reported payments over time. There is no guaranteed timeline and it varies by situation. Anyone promising a number of points by a date is offering something they cannot deliver.',
        },
        {
          q: 'Does this remove the negative marks I already have?',
          a: 'No. No credit builder product removes accurate, verifiable negative information from a credit report. It builds new history; it does not erase the old.',
        },
        {
          q: 'Does it guarantee I get approved for a card or a car later?',
          a: 'No. No product can guarantee approval for another, and a score increase is not guaranteed either: bureaus calculate scores from many factors. What it does is generate reported payment history, one of the factors lenders consider.',
        },
        {
          q: 'What if I cancel after two months?',
          a: 'There is a refund within the first 30 days. Past that point you lose the main benefit, because history is built through sustained payments: an account opened and closed quickly contributes very little.',
        },
        {
          q: 'Why does Blinkblis recommend this?',
          a: 'Because for someone starting from zero, or stuck without movement for years, it meets the four criteria we evaluate: no prior history required, low and fixed cost, reporting to all four bureaus, and clear terms before you hand over personal data. We earn a commission if you open an account, and that is disclosed on every page.',
        },
      ],
    },
  },
]

// Resuelve una oferta por su slug en español o en inglés.
export const getOffer = (slug?: string) =>
  OFFERS.find((o) => o.slug === slug || o.enSlug === slug)

// Ruta canónica de una oferta según el idioma.
export const offerPath = (offer: Offer, lang: Lang) =>
  lang === 'en' ? `/offer/${offer.enSlug}` : `/oferta/${offer.slug}`
